import { Colors, Text } from '@amelio/ui-library';

import type { TranslationKey } from '@/i18n-key';
import type { ContentLanguage } from '@/utils/contentLanguage';

import { useAmelioTranslations } from '@/hooks';

interface WrittenInLanguageLabelProps {
    /** The one language the content is written in; nothing is shown while it is unknown. */
    contentLanguage: ContentLanguage | null | undefined;
    /** The reader's language: the label shows only when the content is written in the other one. */
    readerLanguage: ContentLanguage;
    /** The sentence to show for each language the content can be written in. */
    labels: Record<ContentLanguage, TranslationKey>;
    dataTestId: string;
    className?: string;
}

/**
 * Says which language a piece of AI content is written in, for a reader whose own language is the other one: the
 * content keeps the language it was generated or edited in, and is never translated for its reader.
 */
export const WrittenInLanguageLabel = ({
    contentLanguage,
    readerLanguage,
    labels,
    dataTestId,
    className = 'mt-2',
}: WrittenInLanguageLabelProps) => {
    const { t } = useAmelioTranslations();

    if (!contentLanguage || contentLanguage === readerLanguage) {
        return null;
    }

    return (
        <Text
            size={'small'}
            color={Colors.BASE_500}
            className={className}
            data-testid={dataTestId}
        >
            {t(labels[contentLanguage])}
        </Text>
    );
};
